/* eslint-disable @atlaskit/ui-styling-standard/no-important-styles */
import React, { useEffect, useState } from 'react';

import { cssMap as nativeCssMap } from '@compiled/react';
import { ErrorBoundary } from 'react-error-boundary';
import { di } from 'react-magnetic-di';

import { cssMap, cx } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { type useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';
import { type ActionContext } from '@atlassian/conversation-assistant-message-actions-api/types';
import { ChatGenericError } from '@atlassian/conversation-assistant-ui-components/ui/ChatGenericError';

import type { Build, PlanState } from '../types';
import { isFullyBuilt } from '../utils';

import { CardContent } from './card-content';
import { CardFooter } from './card-footer';
import { CardHeader } from './card-header';
import { SolutionPlanContext } from './solution-plan-context';

const ANIMATION_DURATION = '350ms'; // no token exists for this yet, it seems. { durations.medium } from '@atlaskit/motion' === 350ms

const styles = cssMap({
	container: {
		// @ts-expect-error - cssMap type union does not include !important token strings; required for override specificity
		borderWidth: `${token('border.width')} !important`,
		borderStyle: 'solid !important',
		// @ts-expect-error - cssMap type union does not include !important token strings; required for override specificity
		borderColor: `${token('color.border')} !important`,
		borderRadius: token('radius.xlarge'),
	},
	contentFooterContainer: {
		overflow: 'hidden',
		maxHeight: '0px',
		transitionProperty: 'max-height',
		transitionTimingFunction: token('motion.easing.inout.bold', 'ease-in-out'),
		transitionDuration: ANIMATION_DURATION,
	},
	contentFooterContainer_opened: {
		maxHeight: 'max-content',
	},
});

const nativeStyles = nativeCssMap({
	contentFooterContainer: {
		interpolateSize: 'allow-keywords',
	},
});

export type PlanCardProps = {
	isLoading: boolean;
	name: string;
	solutionVersion: string;
	builds: Build[];
	productKey: string;
	cloudId: string;
	aaid?: string;
	spaceId?: string;
	refetchSolutionPlan: () => void;
	currentBuildNumber: number;
	solutionId: string;
	workspaceId?: string; // TODO Remove optional when cleaning fe_enable_sa_externally
	renderingTouchpoint?: ActionContext['renderingTouchpoint'];
	experienceTracker: ReturnType<typeof useAIMateExperienceTracker> | null;
	solutionState: PlanState;
};

export const PlanCard = ({
	isLoading,
	name,
	solutionVersion,
	builds,
	productKey,
	cloudId,
	aaid,
	spaceId,
	solutionId,
	refetchSolutionPlan,
	currentBuildNumber,
	workspaceId,
	renderingTouchpoint,
	experienceTracker,
	solutionState,
}: PlanCardProps): React.JSX.Element => {
	di();

	const [isOpen, setIsOpen] = useState(false);

	useEffect(() => {
		setIsOpen(!isFullyBuilt(builds));
	}, [builds]);

	useEffect(() => {
		if (!isLoading && builds.length > 0) {
			// Error boundary could throw error. Before succeeding the experience, we want to make sure any error is captured in the experience tracker
			setTimeout(() => {
				experienceTracker?.succeed({
					name: ExperienceName.STUDIO_TRIGGER_SOLUTION_PLAN,
				});
			}, 10);
		} else if (!isLoading && builds.length === 0) {
			experienceTracker?.fail({
				name: ExperienceName.STUDIO_TRIGGER_SOLUTION_PLAN,
				error: new Error('No builds found for solution plan'),
			});
		}
	}, [isLoading, builds, experienceTracker]);

	const handleErrorBoundaryError = (error: Error) => {
		experienceTracker?.fail({
			name: ExperienceName.STUDIO_TRIGGER_SOLUTION_PLAN,
			error,
		});
	};

	return (
		<ErrorBoundary
			fallback={<ChatGenericError errorType="generic" />}
			onError={handleErrorBoundaryError}
		>
			<SolutionPlanContext.Provider
				value={{
					builds,
					refetchSolutionPlan,
					currentBuildNumber,
					cloudId,
					aaid,
					spaceId,
					...(fg('fe_enable_sa_externally') && {
						workspaceId,
						renderingTouchpoint,
					}),
					solutionState,
				}}
			>
				<Box
					xcss={styles.container}
					testId="studio-trigger-solution-build-plan-card"
					data-solution-id={solutionId}
				>
					<CardHeader
						isLoading={isLoading}
						name={name}
						solutionVersion={solutionVersion}
						isOpen={isOpen}
						onToggle={() => setIsOpen(!isOpen)}
					/>
					{!isLoading && (
						<Box
							xcss={cx(
								styles.contentFooterContainer,
								isOpen && styles.contentFooterContainer_opened,
								nativeStyles.contentFooterContainer,
							)}
						>
							<CardContent productKey={productKey} siteId={cloudId} />
							<CardFooter />
						</Box>
					)}
				</Box>
			</SolutionPlanContext.Provider>
		</ErrorBoundary>
	);
};
